import React, { useState, useEffect, useRef } from 'react';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsIcon from '@mui/icons-material/Notifications';
import api from '../../services/api';

const Topbar = ({ user, onLogout, activeTab, onMenuClick }) => {
  const [dueRisks, setDueRisks] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const notifRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get('/risks/due-reviews').then(r => setDueRisks(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!user) return null;

  const getDisplayName = (tab) => {
    switch (tab) {
      case 'dashboard': return 'Risk & Compliance';
      case 'she': return 'SHE Compliance';
      case 'risks': return 'Risk Registers';
      case 'assessment': return 'Risk Assessment';
      case 'controls': return 'Controls & Treatment';
      case 'montecarlo': return 'Monte Carlo Analysis';
      case 'bcm': return 'Business Continuity';
      case 'reports': return 'Analysis & Reports';
      case 'losses': return 'Loss Events';
      default: return tab.replace('-', ' ');
    }
  };

  return (
    <header className="topbar glass">
      <div className="topbar-left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Mobile Toggle Button - visibility controlled by CSS */}
        <button 
          className="mobile-menu-btn" 
          onClick={onMenuClick}
          style={{ 
            background: 'transparent', 
            border: 'none', 
            color: 'white', 
            cursor: 'pointer',
            padding: '8px'
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
        </button>
        <h1 className="topbar-title" style={{ textTransform: 'capitalize', margin: 0 }}>
          {getDisplayName(activeTab)}
        </h1>
      </div>
      
      <div className="topbar-right">
        <div className="user-info-brief hide-on-mobile">
          <div className="user-details" style={{ marginRight: '15px' }}>
            <span className="user-name" style={{ fontWeight: 700, fontSize: '13px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
              <PersonIcon style={{ fontSize: '16px' }} />
              {user.name || 'User Account'}
            </span>
            <span className="user-email" style={{ fontSize: '11px', opacity: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', marginTop: '2px' }}>
              <EmailIcon style={{ fontSize: '12px' }} />
              {user.email}
            </span>
          </div>
        </div>
        
        {/* Notification Bell */}
        <div ref={notifRef} style={{ position: 'relative', marginRight: '8px' }}>
          <button
            onClick={() => setShowNotifs(v => !v)}
            title="Risk Review Notifications"
            style={{ position: 'relative', background: 'transparent', border: 'none', color: dueRisks.length ? '#f59e0b' : '#94a3b8', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center' }}
          >
            <NotificationsIcon style={{ fontSize: '22px' }} />
            {dueRisks.length > 0 && (
              <span style={{ position: 'absolute', top: 0, right: 0, background: '#ef4444', color: 'white', borderRadius: '50%', fontSize: '10px', fontWeight: 'bold', minWidth: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                {dueRisks.length}
              </span>
            )}
          </button>

          {showNotifs && (
            <div style={{ position: 'absolute', right: 0, top: '36px', width: '340px', background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'white', fontWeight: 'bold', fontSize: '13px' }}>Upcoming Risk Reviews</span>
                <span style={{ color: '#f59e0b', fontSize: '11px', fontWeight: 'bold' }}>{dueRisks.length} due within 20 days</span>
              </div>
              <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                {dueRisks.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>No upcoming reviews</div>
                ) : dueRisks.map(r => (
                  <div key={r.id} style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <span style={{ color: '#10b981', fontWeight: 'bold', fontSize: '12px' }}>{r.sn}</span>
                      <span style={{ color: '#f59e0b', fontSize: '11px', fontWeight: 'bold' }}>Due {r.resolved_by}</span>
                    </div>
                    <div style={{ color: '#cbd5e1', fontSize: '12px', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.risk_description}</div>
                    <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>{r.department_id} · {r.owner}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <button className="btn-logout-top" onClick={onLogout} title="Logout">
          <LockIcon style={{ fontSize: '16px' }} />
          <span className="logout-text hide-on-mobile">Logout</span>
        </button>
      </div>
    </header>
  );
};

export default Topbar;
